<template>
  <div class="space-y-6">
    <!-- Header Section -->
    <div class="bg-gradient-to-r from-red-500 to-pink-600 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Salud</h1>
          <p class="text-red-100 mt-1">Gestiona los artículos de salud para mascotas</p>
        </div>
        <div class="hidden sm:block">
          <div class="bg-white/20 rounded-lg p-4">
            <svg class="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-red-100">
            <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Total Artículos</p>
            <p class="text-2xl font-semibold text-gray-900">{{ healthItems?.length || 0 }}</p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100">
            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Con Video</p>
            <p class="text-2xl font-semibold text-gray-900">{{ healthItems?.filter(item => item.video).length || 0 }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="card p-6">
      <div class="flex flex-col lg:flex-row gap-4 items-end">
        <!-- Search Filter -->
        <div class="w-full lg:flex-1">
          <label class="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
          <InputText
            v-model="globalFilter"
            placeholder="Buscar por título o descripción..."
            class="w-full"
          />
        </div>

        <!-- Animal Type Filter -->
        <div class="w-full lg:w-48">
          <label class="block text-sm font-medium text-gray-700 mb-2">Tipo de animal</label>
          <select
            v-model="selectedAnimalTypeId"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none bg-white"
          >
            <option :value="null">Todos</option>
            <option v-for="type in animalTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
          </select>
        </div>

        <!-- Add Button -->
        <div class="flex-shrink-0">
          <button
            @click="router.push('/health/create')"
            class="inline-flex items-center px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-medium rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Agregar Artículo
          </button>
        </div>
      </div>
    </div>

    <!-- DataTable with PrimeVue -->
    <div class="card">
      <DataTable
        :value="filteredHealthItems"
        :paginator="true"
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        :loading="loading"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} a {last} de {totalRecords} artículos"
        responsiveLayout="scroll"
        class="p-datatable-sm"
      >
        <template #empty>
          <div class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No hay artículos</h3>
            <p class="mt-1 text-sm text-gray-500">Comienza creando un nuevo artículo de salud.</p>
          </div>
        </template>

        <Column field="image" header="Imagen" sortable style="width: 80px; min-width: 80px;">
          <template #body="{ data }">
            <img
              v-if="data.image"
              :src="data.image"
              alt="Imagen de salud"
              class="w-16 h-16 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
              @click="openImageModal(data.image, data.title)"
            />
            <img
              v-else
              src="/images/empty_image.png"
              alt="Sin imagen"
              class="w-16 h-16 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
              @click="openImageModal('/images/empty_image.png', data.title)"
            />
          </template>
        </Column>

        <Column field="title" header="Título" sortable style="width: auto; flex: 1;">
          <template #body="{ data }">
            <div>
              <div class="text-sm font-medium text-gray-900">{{ data.title }}</div>
              <div class="text-sm text-gray-500">{{ data.description ? data.description.substring(0, 50) + '...' : 'Sin descripción' }}</div>
            </div>
          </template>
        </Column>

        <Column field="video" header="Video" sortable style="width: 100px; min-width: 100px;">
          <template #body="{ data }">
            <div v-if="data.video" class="relative">
              <video
                :src="data.video"
                class="w-24 h-16 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                @click="openVideoModal(data.video, data.title)"
                muted
              >
                Tu navegador no soporta el elemento video.
              </video>
              <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg class="w-6 h-6 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
            <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              Sin video
            </span>
          </template>
        </Column>



        <Column header="Tipo de Animal" style="width: 140px; min-width: 120px;">
          <template #body="{ data }">
            <span v-if="getAnimalTypeName(data.animalTypeId, data.animalType)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="getAnimalTypeBadgeClass(data.animalTypeId, data.animalType)">
              {{ getAnimalTypeName(data.animalTypeId, data.animalType) }}
            </span>
            <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              Todos
            </span>
          </template>
        </Column>

        <Column header="Acciones" :exportable="false">
          <template #body="{ data }">
            <div class="flex items-center space-x-2">
              <button
                @click="editHealth(data)"
                class="inline-flex items-center p-2 border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style="background-color: #1d4ed8; color: #fff"
                title="Editar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                @click="deleteHealth(data)"
                class="inline-flex items-center p-2 border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                style="background-color: #be123c; color: #fff"
                title="Eliminar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Delete Confirmation Modal -->
    <ConfirmModal
      :show="showDeleteModal"
      title="Eliminar Artículo"
      :message="`¿Estás seguro de que deseas eliminar el artículo '${selectedHealth?.title}'? Esta acción no se puede deshacer.`"
      confirmText="Eliminar"
      confirmClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
      @confirm="confirmDelete"
      @cancel="showDeleteModal = false"
    />

    <!-- Image Modal -->
    <ImageModal
      :show="showImageModal"
      :image-url="selectedImage"
      :image-alt="selectedImageAlt"
      @close="closeImageModal"
    />

    <VideoModal
      :show="showVideoModal"
      :video-url="selectedVideo"
      :title="selectedVideoTitle"
      @close="closeVideoModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

import InputText from 'primevue/inputtext'
import type { Health } from '@/types'
import { healthService } from '@/services/healthService'
import { useNotifications } from '@/composables/useNotifications'
import { animalTypeService } from '@/services/animalTypeService'
import ConfirmModal from '@/components/ConfirmModal.vue'
import ImageModal from '@/components/ImageModal.vue'
import VideoModal from '@/components/VideoModal.vue'

const { showNotification } = useNotifications()
const router = useRouter()

const loading = ref(false)
const healthItems = ref<Health[]>([])
const selectedHealth = ref<Health | null>(null)
const showDeleteModal = ref(false)
const showImageModal = ref(false)
const selectedImage = ref('')
const selectedImageAlt = ref('')
const showVideoModal = ref(false)
const selectedVideo = ref('')
const selectedVideoTitle = ref('')
const globalFilter = ref('')
const animalTypes = ref<{ id: number; name: string }[]>([])
const selectedAnimalTypeId = ref<number | null>(null)



const filteredHealthItems = computed(() => {
  let items = healthItems.value
  
  if (selectedAnimalTypeId.value !== null) {
    items = items.filter(item => item.animalTypeId === selectedAnimalTypeId.value)
  }
  
  if (globalFilter.value) {
    const searchTerm = globalFilter.value.toLowerCase()
    items = items.filter(item => 
      item.title.toLowerCase().includes(searchTerm) ||
      (item.description && item.description.toLowerCase().includes(searchTerm))
    )
  }
  
  return items
})

const getAnimalTypeName = (animalTypeId: number | null | undefined, animalType?: { name?: string } | null) => {
  if (animalType?.name) {
    return animalType.name
  }

  if (animalTypeId === null || animalTypeId === undefined) {
    return null
  }

  const found = animalTypes.value.find(type => type.id === animalTypeId)
  return found?.name || null
}

const getAnimalTypeBadgeClass = (animalTypeId: number | null | undefined, animalType?: { name?: string } | null) => {
  const name = (getAnimalTypeName(animalTypeId, animalType) || '').toLowerCase()

  if (name.includes('perro')) return 'bg-blue-100 text-blue-800'
  if (name.includes('gato')) return 'bg-orange-100 text-orange-800'
  if (name.includes('ave')) return 'bg-sky-100 text-sky-800'
  if (name.includes('pez')) return 'bg-cyan-100 text-cyan-800'
  if (name.includes('conejo')) return 'bg-pink-100 text-pink-800'

  return 'bg-green-100 text-green-800'
}

const loadHealthItems = async () => {
  loading.value = true
  try {
    const response = await healthService.getAllHealth()
    healthItems.value = response.data || []
  } catch (error: any) {
    showNotification({ message: 'Error al cargar los artículos de salud', type: 'error' })
    healthItems.value = []
  } finally {
    loading.value = false
  }
}

const editHealth = (health: Health) => {
  router.push(`/health/${health.id}/edit`)
}

const deleteHealth = (health: Health) => {
  selectedHealth.value = health
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (!selectedHealth.value) return
  
  try {
    await healthService.deleteHealth(selectedHealth.value.id)
    await loadHealthItems()
    showNotification({ message: 'Artículo eliminado exitosamente', type: 'success' })
  } catch (error: any) {
    console.error('Error deleting health item:', error)
    showNotification({ message: 'Error al eliminar el artículo', type: 'error' })
  } finally {
    showDeleteModal.value = false
    selectedHealth.value = null
  }
}

const openVideoModal = (videoUrl: string, title: string) => {
  selectedVideo.value = videoUrl
  selectedVideoTitle.value = title
  showVideoModal.value = true
}

const closeVideoModal = () => {
  showVideoModal.value = false
  selectedVideo.value = ''
  selectedVideoTitle.value = ''
}

const openImageModal = (imageUrl: string, imageAlt: string) => {
  selectedImage.value = imageUrl
  selectedImageAlt.value = imageAlt
  showImageModal.value = true
}

const closeImageModal = () => {
  showImageModal.value = false
  selectedImage.value = ''
  selectedImageAlt.value = ''
}

onMounted(async () => {
  loadHealthItems()
  try {
    const res = await animalTypeService.getAllAnimalTypes()
    animalTypes.value = res.data || res
  } catch {}
})
</script>